"use client";

import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, CalendarX } from "lucide-react";
import type { SlotOption } from "@/lib/scheduling/types";
import { formatDateKeySeoul } from "@/lib/scheduling/slotMath";

interface LessonCalendarPickerProps {
  instructorId: string;
  durationMinutes: number;
  selectedSlot: SlotOption | null;
  onSelect: (slot: SlotOption | null) => void;
  theme?: "light" | "dark";
}

const DAY_LABELS = ["일", "월", "화", "수", "목", "금", "토"];

export default function LessonCalendarPicker({
  instructorId,
  durationMinutes,
  selectedSlot,
  onSelect,
  theme = "light",
}: LessonCalendarPickerProps) {
  const [loading, setLoading] = useState(true);
  const [slotsByDate, setSlotsByDate] = useState<Map<string, SlotOption[]>>(
    new Map()
  );
  const today = useMemo(() => new Date(), []);
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState<string | null>(
    selectedSlot?.date || null
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          service: "lesson",
          durationMinutes: String(durationMinutes),
          instructorId,
        });
        const res = await fetch(`/api/scheduling/slots?${params.toString()}`, {
          cache: "no-store",
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "슬롯 조회 실패");
        if (cancelled) return;
        const map = new Map<string, SlotOption[]>();
        for (const slot of (data.slots || []) as SlotOption[]) {
          const list = map.get(slot.date) || [];
          list.push(slot);
          map.set(slot.date, list);
        }
        setSlotsByDate(map);
      } catch (error) {
        console.error("레슨 슬롯 조회 실패:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [instructorId, durationMinutes]);

  const calendarCells = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth, 1);
    const startOffset = firstDay.getDay();
    const cells: { date: Date; inMonth: boolean }[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(viewYear, viewMonth, 1 - startOffset + i);
      cells.push({ date: d, inMonth: d.getMonth() === viewMonth });
    }
    while (cells.length > 35 && cells.slice(35).every(c => !c.inMonth)) {
      cells.splice(35);
    }
    return cells;
  }, [viewYear, viewMonth]);

  const todayKey = formatDateKeySeoul(today);
  const daySlots = selectedDate ? slotsByDate.get(selectedDate) || [] : [];
  const morningSlots = daySlots.filter(s => Number(s.time.split(":")[0]) < 12);
  const afternoonSlots = daySlots.filter(
    s => Number(s.time.split(":")[0]) >= 12
  );

  const isDark = theme === "dark";

  if (loading) {
    return (
      <div
        className={`flex items-center justify-center py-10 ${
          isDark ? "text-gray-400" : "text-gray-500"
        }`}
      >
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        예약 가능한 시간을 불러오는 중...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div
        className={`rounded-2xl border p-4 ${
          isDark
            ? "border-white/10 bg-white/5"
            : "border-gray-200 bg-white"
        }`}
      >
        <div className="mb-3 flex items-center justify-between">
          <button
            type="button"
            onClick={() => {
              const d = new Date(viewYear, viewMonth - 1, 1);
              if (
                d.getFullYear() < today.getFullYear() ||
                (d.getFullYear() === today.getFullYear() &&
                  d.getMonth() < today.getMonth())
              ) {
                return;
              }
              setViewYear(d.getFullYear());
              setViewMonth(d.getMonth());
            }}
            className={`rounded-lg p-1.5 ${
              isDark ? "hover:bg-white/10" : "hover:bg-gray-100"
            }`}
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <p className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
            {viewYear}년 {viewMonth + 1}월
          </p>
          <button
            type="button"
            onClick={() => {
              const d = new Date(viewYear, viewMonth + 1, 1);
              setViewYear(d.getFullYear());
              setViewMonth(d.getMonth());
            }}
            className={`rounded-lg p-1.5 ${
              isDark ? "hover:bg-white/10" : "hover:bg-gray-100"
            }`}
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-1">
          {DAY_LABELS.map(label => (
            <div
              key={label}
              className={`text-center text-[11px] font-medium py-1 ${
                isDark ? "text-gray-400" : "text-gray-500"
              }`}
            >
              {label}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {calendarCells.map(({ date, inMonth }) => {
            const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
            const hasSlots = (slotsByDate.get(key)?.length || 0) > 0;
            const selected = selectedDate === key;
            const isPast = key < todayKey;
            return (
              <button
                key={key}
                type="button"
                disabled={!inMonth || isPast || !hasSlots}
                onClick={() => {
                  setSelectedDate(key);
                  onSelect(null);
                }}
                className={`aspect-square rounded-xl text-sm transition ${
                  selected
                    ? "bg-violet-600 text-white font-bold"
                    : hasSlots && inMonth && !isPast
                      ? isDark
                        ? "bg-violet-500/20 text-violet-100 hover:bg-violet-500/30"
                        : "bg-violet-50 text-violet-800 hover:bg-violet-100 font-medium"
                      : isDark
                        ? "text-gray-600"
                        : "text-gray-300"
                } ${!inMonth ? "opacity-30" : ""}`}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
      </div>

      {selectedDate ? (
        daySlots.length === 0 ? (
          <div
            className={`flex items-center gap-2 rounded-xl border px-4 py-6 text-sm ${
              isDark
                ? "border-white/10 text-gray-400"
                : "border-gray-200 text-gray-500"
            }`}
          >
            <CalendarX className="h-4 w-4" />
            이 날짜에는 가능한 시간이 없습니다.
          </div>
        ) : (
          <div className="space-y-4">
            {[
              { label: "오전", slots: morningSlots },
              { label: "오후", slots: afternoonSlots },
            ].map(group =>
              group.slots.length === 0 ? null : (
                <div key={group.label}>
                  <p
                    className={`mb-2 text-xs font-semibold ${
                      isDark ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {group.label}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {group.slots.map(slot => {
                      const active =
                        selectedSlot?.date === slot.date &&
                        selectedSlot?.time === slot.time;
                      return (
                        <button
                          key={`${slot.date}-${slot.time}`}
                          type="button"
                          onClick={() => onSelect(slot)}
                          className={`rounded-xl px-3 py-2 text-sm font-semibold transition ${
                            active
                              ? "bg-violet-600 text-white"
                              : isDark
                                ? "border border-white/15 bg-white/5 text-white hover:border-violet-400/50"
                                : "border border-gray-200 bg-white text-gray-800 hover:border-violet-300"
                          }`}
                        >
                          {slot.time}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )
            )}
          </div>
        )
      ) : (
        <p
          className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}
        >
          날짜를 선택하면 가능한 시간이 표시됩니다.
        </p>
      )}
    </div>
  );
}
